import React from 'react'
import { ColumnDefinition, ColumnGroupDefinition, TableRowData } from '../types'
import { WorkPeriodWithMission, WorkPeriodWithSummary } from '../../../../api/work-periods'
import { formatDay, getWeekdayMondayBased } from '../../../../utils/date'
import { createColumns } from '../utils'
import { WeekSummary } from '../../../../../../backend/src/services/resources/missions/missions.summary.service.types'
import { getDay } from 'date-fns'
import Protected, { isAuthorizedFor } from '../../../protected/protected'
import WorkPeriodValidateButton from '../../../work-periods/validate-button'
import WorkPeriodLink from '../../../work-periods/work-period-link'
import { getDocIdAsString } from '../../../../utils/documents'
import {
  WorkPeriodAbandonedMessage,
  WorkPeriodEventsMessage,
  WorkPeriodTagsMessage,
} from '../../../work-periods/work-period-messages'
import { DurationCell, EmployerRuleCell, HourCell } from '../table-cells'
import { Element, Icon } from 'react-bulma-components'
import {
  getWorkPeriodElementColor,
  getWorkPeriodFromDayDate,
  getWorkPeriodCellData,
} from '../../../../utils/work-periods'
import { WorkPeriodSummaryDateTooltipContent } from '../../../work-periods/tooltips/work-period-summary-date-tooltip-content'
import { WorkPeriodSummaryBreaksTooltipContent } from '../../../work-periods/tooltips/work-period-summary-breaks-tooltip-content'
import { UserRemoveIcon } from '@heroicons/react/outline'
import { SpecialPeriod } from '../../../../api/special-periods'

interface DayColumnsGroupProps {
  day: Date
  dayIndex: number
  handleValidation?: (workPeriod: WorkPeriodWithMission) => void
  hideActions?: boolean
  minimalMode?: boolean
  employerRules: WeekSummary['employerRules']
  isDayWithoutWorkPeriods?: boolean
  specialPeriods: SpecialPeriod[]
}

/**
 * Utility function to safely get work period data
 * @param data - The row data containing mission information
 * @param dayIndex - The index of the day to get work period for
 * @returns The work period if it exists, null otherwise
 */
const getWorkPeriod = (data: TableRowData, day: Date): WorkPeriodWithSummary | null => {
  const workPeriod = getWorkPeriodFromDayDate(data.mission.workPeriods, day) as
    | WorkPeriodWithSummary
    | undefined
  return workPeriod || null
}

/**
 * Creates a render function for work period cells.
 *
 * For employer members the cell is wrapped in a WorkPeriodLink that handles
 * both real and virtual (template-based) work periods. Virtual WPs are
 * materialized on click before navigating to the detail page.
 *
 * @param day - The calendar day this column represents
 * @param renderFn - Function to render the cell content
 * @returns A render function for the cell
 */
const renderWorkPeriodCell = (
  day: Date,
  renderFn: (workPeriod: WorkPeriodWithSummary, data: any) => React.ReactNode,
) => {
  return (data: TableRowData) => {
    const workPeriod = getWorkPeriod(data, day)

    if (!workPeriod) return null

    const strikethroughStyle =
      workPeriod.isCancelled || workPeriod.isAbandoned
        ? { textDecoration: 'line-through', textDecorationThickness: '1px' }
        : {}

    // Employer members get a clickable link (handles virtual WP materialization)
    if (isAuthorizedFor(['employerMember'])) {
      return (
        <WorkPeriodLink workPeriod={workPeriod} textColor="dark" style={strikethroughStyle}>
          <Element renderAs="span">{renderFn(workPeriod, data)}</Element>
        </WorkPeriodLink>
      )
    }

    // Other roles: plain text, no link
    return (
      <Element renderAs="span" textColor="dark" style={strikethroughStyle}>
        <Element renderAs="span">{renderFn(workPeriod, data)}</Element>
      </Element>
    )
  }
}

const createWorkPeriodColumnsGroup = ({
  day,
  dayIndex,
  handleValidation,
  hideActions,
  minimalMode,
  employerRules,
  isDayWithoutWorkPeriods,
  specialPeriods,
}: DayColumnsGroupProps): ColumnGroupDefinition => {
  const specialPeriodsNames = specialPeriods?.map(specialPeriod => specialPeriod.name).join(', ')
  const header = specialPeriodsNames ? `${formatDay(day)} (${specialPeriodsNames})` : formatDay(day)

  const buildClassName = (className = '') => {
    if (specialPeriodsNames) {
      className += ' has-background-primary-light'
    }
    return className
  }

  const employerRulesColumns: ColumnDefinition[] = employerRules.map(rule => ({
    header: rule.shortName,
    id: `day-${dayIndex}-${rule.name}-${rule.ruleId}`,
    isHidden: minimalMode,
    className: buildClassName(),
    render: data => {
      const workPeriod = getWorkPeriod(data, day)
      const weekDayNumber = getWeekdayMondayBased(getDay(day))
      const value =
        rule.kind === 'goal'
          ? (data.totals.days[weekDayNumber]?.employerData.goals[getDocIdAsString(rule.ruleId)] ??
            null)
          : (data.totals.days[weekDayNumber]?.employerData.durations[
              getDocIdAsString(rule.ruleId)
            ] ?? null)
      if (!workPeriod && !value) return null
      return (
        <EmployerRuleCell
          employerRule={rule}
          value={value}
          textColor={workPeriod ? getWorkPeriodElementColor(workPeriod, 'other') : ''}
        />
      )
    },
  }))

  if (isDayWithoutWorkPeriods) {
    return {
      Header: formatDay(day),
      id: `day-${dayIndex}`,
      columns: createColumns([
        {
          header: (
            <Element>
              <Icon
                data-tooltip="Jour sans périodes de travail"
                size="small"
                color="grey"
                style={{ marginRight: '2.0rem' }}
              >
                <UserRemoveIcon />
              </Icon>
            </Element>
          ),
          id: `day-${dayIndex}-no-work-periods`,
          className: 'table-section-start',
        },
      ]),
    }
  }

  return {
    Header: header,
    id: `day-${dayIndex}`,
    columns: createColumns([
      {
        header: 'Début',
        id: `day-${dayIndex}-start`,
        className: buildClassName('table-section-start'),
        render: renderWorkPeriodCell(day, workPeriod => {
          const cellData = getWorkPeriodCellData(workPeriod, 'start')
          return (
            <HourCell
              date={cellData.date}
              textColor={cellData.color}
              tooltipId={cellData.tooltipId}
              tooltipContent={
                <WorkPeriodSummaryDateTooltipContent workPeriod={workPeriod} type="start" />
              }
            />
          )
        }),
      },
      {
        header: 'Fin',
        id: `day-${dayIndex}-end`,
        className: buildClassName(),
        render: renderWorkPeriodCell(day, workPeriod => {
          const cellData = getWorkPeriodCellData(workPeriod, 'end')
          return (
            <HourCell
              date={cellData.date}
              textColor={cellData.color}
              tooltipId={cellData.tooltipId}
              tooltipContent={
                <WorkPeriodSummaryDateTooltipContent workPeriod={workPeriod} type="end" />
              }
            />
          )
        }),
      },
      {
        header: 'Pause',
        id: `day-${dayIndex}-break`,
        isHidden: minimalMode,
        className: buildClassName(),
        render: renderWorkPeriodCell(day, workPeriod => {
          const cellData = getWorkPeriodCellData(workPeriod, 'break')
          return (
            <DurationCell
              duration={workPeriod.summary.durations.breaks}
              tooltipContent={<WorkPeriodSummaryBreaksTooltipContent workPeriod={workPeriod} />}
              tooltipId={cellData.tooltipId}
              textColor={cellData.color}
            />
          )
        }),
      },
      {
        header: 'Jour',
        id: `day-${dayIndex}-day`,
        isHidden: minimalMode,
        className: buildClassName(),
        render: renderWorkPeriodCell(day, workPeriod => {
          const color = getWorkPeriodElementColor(workPeriod, 'other')
          return (
            <DurationCell duration={workPeriod.summary.durations.worked.day} textColor={color} />
          )
        }),
      },
      {
        header: 'Nuit',
        id: `day-${dayIndex}-night`,
        isHidden: minimalMode,
        className: buildClassName(),
        render: renderWorkPeriodCell(day, workPeriod => {
          const color = getWorkPeriodElementColor(workPeriod, 'other')
          return (
            <DurationCell duration={workPeriod.summary.durations.worked.night} textColor={color} />
          )
        }),
      },
      {
        header: 'N.Effectif',
        id: `day-${dayIndex}-benefit`,
        isHidden: minimalMode,
        className: buildClassName(),
        render: renderWorkPeriodCell(day, workPeriod => {
          const color = getWorkPeriodElementColor(workPeriod, 'event')
          return <DurationCell duration={workPeriod.summary.durations.benefit} textColor={color} />
        }),
      },
      ...employerRulesColumns,
      {
        header: (
          // @ts-expect-error
          <WorkPeriodTagsMessage workPeriod={{ tags: [{}] }} type="icon" small text="Catégories" />
        ),
        id: `day-${dayIndex}-tags`,
        isHidden: minimalMode,
        className: buildClassName(),
        render: renderWorkPeriodCell(day, workPeriod => {
          return (
            <Element mt={1}>
              <WorkPeriodTagsMessage workPeriod={workPeriod} type="icon" small />
            </Element>
          )
        }),
      },
      {
        header: (
          <WorkPeriodAbandonedMessage
            workPeriod={{ isAbandoned: true }}
            type="icon"
            small
            iconColor="dark"
          />
        ),
        id: `day-${dayIndex}-interruption`,
        className: buildClassName(),
        render: renderWorkPeriodCell(day, workPeriod => (
          <WorkPeriodAbandonedMessage workPeriod={workPeriod} type="icon" small />
        )),
      },
      {
        id: `day-${dayIndex}-events`,
        header: (
          <WorkPeriodEventsMessage
            workPeriod={{
              events: [
                {
                  item: {
                    type: 'benefit',
                    _id: 'benefit',
                    code: 'label',
                    title: 'info',
                    employer: '_id',
                    indicatesAbsence: true,
                  },
                  comment: '',
                },
              ],
            }}
            text="Evènements"
            type="icon"
            small
            iconColor={'dark'}
          />
        ),
        className: buildClassName(),
        render: renderWorkPeriodCell(day, workPeriod => {
          const color = getWorkPeriodElementColor(workPeriod, 'event')
          return (
            <Element mt={1}>
              <WorkPeriodEventsMessage
                workPeriod={workPeriod}
                type="icon"
                small
                iconColor={color}
              />
            </Element>
          )
        }),
      },
      {
        header: 'Valider',
        id: `day-${dayIndex}-validated`,
        isHidden: hideActions,
        className: buildClassName(),
        render: renderWorkPeriodCell(day, (workPeriod, data) => (
          <Protected
            roles={['employerMember']}
            action={{ resource: 'missions', name: 'validateWorkPeriod' }}
          >
            <Element renderAs="span" onClick={e => e.preventDefault()}>
              <WorkPeriodValidateButton
                workPeriod={workPeriod}
                label=""
                type="button"
                size="smallest"
                onClick={workPeriod =>
                  handleValidation &&
                  handleValidation({
                    ...workPeriod,
                    mission: { ...data.mission, _id: getDocIdAsString(workPeriod.mission) },
                  })
                }
              />
            </Element>
          </Protected>
        )),
      },
    ]),
  }
}

export default createWorkPeriodColumnsGroup
